import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import contentData from "../../locales/download/translation.json";
import "../../styles/download/index.css";

interface ContentItem {
  lang: string;
  dir: string;
  toggleText: string;
  title: string;
  tagline: string;
  heading: string;
  compatTitle: string;
  compatList: string[];
  fallbackNote: string;
  portalUrl?: string;
  appStoreBadge: string;
  playStoreBadge: string;
  heroImage: string;
  copyright: string;
  terms: string;
  privacy: string;
  termsUrl: string;
  privacyUrl: string;
  helpText?: string;
  helpUrl?: string;
}

interface DownloadProps {
  defaultLanguage?: "ar" | "en";
}

const APP_STORE_URL = "https://apps.apple.com/sa/app/jawwy-%D8%AC%D9%88-%D9%8A/id1670373086";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.jawwyevo_main.app&hl=en&gl=US";

export const Download = ({ defaultLanguage = "ar" }: DownloadProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isEn = location.pathname.endsWith("/en") || location.pathname === "/en";
  const isAr = location.pathname.endsWith("/ar") || location.pathname === "/ar";

  const lang: "ar" | "en" = isEn ? "en" : isAr ? "ar" : defaultLanguage;

  // If the current URL doesn't have /ar or /en, redirect to display it in the URL
  useEffect(() => {
    if (!isEn && !isAr) {
      const basePath = location.pathname.replace(/\/+$/, "");
      navigate(`${basePath}/${lang}`, { replace: true });
    }
  }, [isEn, isAr, lang, location.pathname, navigate]);

  const content = (contentData as Record<string, ContentItem>)[lang] || contentData.ar;

  useEffect(() => {
    document.documentElement.lang = content.lang;
    document.documentElement.dir = content.dir;
    document.title = content.title;
  }, [content]);

  const handleToggleLanguage = () => {
    const nextLang = lang === "ar" ? "en" : "ar";
    if (location.pathname.endsWith(`/${lang}`)) {
      const basePath = location.pathname.slice(0, -(lang.length + 1));
      navigate(`${basePath}/${nextLang}`);
    } else if (location.pathname === `/${lang}`) {
      navigate(`/${nextLang}`);
    } else {
      navigate(`/download/${nextLang}`);
    }
  };

  const helpUrl = content.helpUrl || "https://www.jawwy.sa/content/jawwy/en/help.html";
  const helpText = content.helpText || "Help";
  const portalUrl = `https://www.jawwy.sa/content/jawwy/${lang}/home.html`;

  const renderFallbackNote = () => {
    const targetWord =
      lang === "ar"
        ? "”حسابي”"
        : content.fallbackNote?.includes("”My Account”")
          ? "”My Account”"
          : "Jawwy portal";
    if (content.fallbackNote && content.fallbackNote.includes(targetWord)) {
      const parts = content.fallbackNote.split(targetWord);
      return (
        <p className="jawwy-fallback-note">
          {parts[0]}
          <a
            href={portalUrl}
            target="_blank"
            rel="noreferrer"
            className="jawwy-fallback-link"
          >
            <span style={{ color: '#e55613' }}>{targetWord}</span>
          </a>
          {parts[1]}
        </p>
      );
    }
    return <p className="jawwy-fallback-note">{content.fallbackNote}</p>;
  };

  return (
    <div className={`jawwy-page ${content.dir}`}>
      {/* Header */}
      <header className="jawwy-header">
        <div className="jawwy-header-inner">
          <a href="https://www.jawwy.sa" className="jawwy-logo" aria-label="Jawwy">
            <img src="/assets/download/Logo.svg" alt="Jawwy Logo" />
          </a>
          <button
            type="button"
            className="jawwy-lang-btn"
            onClick={handleToggleLanguage}
            aria-label={lang === "ar" ? "التبديل إلى اللغة الإنجليزية" : "Switch to Arabic language"}
          >
            {content.toggleText}
          </button>
        </div>
      </header>

      {/* Main Hero */}
      <main className="jawwy-main">
        <div className="jawwy-main-inner">
          {/* Left Column: Info & Download Links */}
          <div className="jawwy-content">
            <span className="jawwy-tagline">{content.tagline}</span>
            <h1 className="jawwy-heading">{content.heading}</h1>

            <div className="jawwy-compat-info">
              <p className="jawwy-compat-title">{content.compatTitle}</p>
              <ul className="jawwy-compat-list">
                {content.compatList.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              {renderFallbackNote()}
            </div>

            {/* Store Badges */}
            <div className="jawwy-store-buttons">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="jawwy-store-badge-link"
                aria-label={lang === "ar" ? "تحميل تطبيق جوّي من متجر App Store" : "Download Jawwy app from Apple App Store"}
              >
                <img src={content.appStoreBadge} alt="App Store" />
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="jawwy-store-badge-link"
                aria-label={lang === "ar" ? "تحميل تطبيق جوّي من متجر Google Play" : "Download Jawwy app from Google Play Store"}
              >
                <img src={content.playStoreBadge} alt="Google Play" />
              </a>
            </div>
          </div>

          {/* Right Column: App Mockup Image */}
          <div className="jawwy-hero-image">
            <img src={content.heroImage} alt="Jawwy App Mockup" />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="jawwy-footer">
        <div className="jawwy-footer-inner">
          <div className="jawwy-divider" />
          <div className="jawwy-footer-row">
            <div className="jawwy-footer-brand">
              <img src="/assets/download/logowhite.svg" alt="Jawwy White Logo" className="jawwy-footer-logo" />
              <span>{content.copyright}</span>
            </div>
            <div className="jawwy-footer-links">
              <a href={helpUrl} target="_blank" rel="noreferrer">
                {helpText}
              </a>
              <a href={content.termsUrl} target="_blank" rel="noreferrer">
                {content.terms}
              </a>
              <a href={content.privacyUrl} target="_blank" rel="noreferrer">
                {content.privacy}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Download;
